import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import './WalletOverviewChart.scss';

interface WalletOverviewChartProps {
  period: string;
}

const WalletOverviewChart: React.FC<WalletOverviewChartProps> = ({ period }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Create gradients
    const lineGradient = ctx.createLinearGradient(0, 0, 800, 0);
    lineGradient.addColorStop(0, 'rgba(0, 99, 121, 1)');
    lineGradient.addColorStop(1, 'rgba(0, 174, 212, 1)');

    const areaGradient = ctx.createLinearGradient(0, 0, 0, 200);
    areaGradient.addColorStop(0, 'rgba(0, 174, 212, 0.45)');
    areaGradient.addColorStop(1, 'rgba(0, 99, 121, 0.0)');

    // Sample data
    const dataSets: Record<string, number[]> = {
      '7': [100, 200, 150, 300, 250, 400, 350],
      '30': Array.from({ length: 30 }, () => Math.floor(Math.random() * 500)),
    };

    // Labels
    const labels: Record<string, string[]> = {
      '7': ['23 Oct', '24 Oct', '25 Oct', '26 Oct', '27 Oct', '28 Oct', '29 Oct'],
      '30': Array.from({ length: 30 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (29 - i));
        return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
      }),
    };

    // Vertical line plugin
    const verticalLinePlugin = {
      id: 'verticalLine',
      afterDatasetsDraw(chart: any) {
        if (chart.tooltip._active && chart.tooltip._active.length) {
          const activePoint = chart.tooltip._active[0];
          const ctx = chart.ctx;
          const x = activePoint.element.x;
          const topY = chart.scales.y.top;
          const bottomY = chart.scales.y.bottom;

          ctx.save();
          ctx.setLineDash([5, 5]);
          ctx.beginPath();
          ctx.moveTo(x, topY);
          ctx.lineTo(x, bottomY);
          ctx.lineWidth = 1;
          ctx.strokeStyle = 'rgba(129, 211, 229, 0.3)';
          ctx.stroke();
          ctx.restore();
        }
      },
    };

    // Extended data with empty points at start and end for better visualization
    const currentRange = period;
    const extendedData = [
      dataSets[currentRange][0],
      ...dataSets[currentRange],
      dataSets[currentRange][dataSets[currentRange].length - 1],
    ];

    const extendedLabels = [
      "",
      ...labels[currentRange],
      "",
    ];

    // Destroy previous chart if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Create new chart
    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: extendedLabels,
        datasets: [
          {
            label: 'Wallet Overview',
            data: extendedData,
            fill: true,
            backgroundColor: areaGradient,
            borderColor: lineGradient,
            borderWidth: 3,
            tension: 0.2,
            pointRadius: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            intersect: false,
            backgroundColor: 'rgba(18, 26, 43, 0.9)',
            titleColor: '#fff',
            bodyColor: '#fff',
            padding: 12,
            cornerRadius: 8,
            displayColors: false,
            titleFont: {
              family: 'Manrope',
              size: 14,
              weight: 'bold',
            },
            bodyFont: {
              family: 'Manrope',
              size: 13,
            },
            callbacks: {
              label: function (context) {
                const index = context.dataIndex;
                if (index === 0 || index === extendedData.length - 1) {
                  return '';
                }
                return `Value: ${context.raw}`;
              },
              title: function(tooltipItems) {
                const index = tooltipItems[0].dataIndex;
                if (index === 0 || index === extendedLabels.length - 1) {
                  return '';
                }
                return extendedLabels[index];
              }
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: '#4E6D8A',
              font: {
                family: 'Manrope',
                size: 12,
                weight: 'bold',
              },
              padding: 0,
            },
            offset: false,
          },
          y: {
            grid: { display: false },
            display: false,
          },
        },
        layout: {
          padding: {
            top: 15,
            bottom: 15,
            left: 0,
            right: 0,
          },
        },
      },
      plugins: [verticalLinePlugin],
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [period]);

  return <canvas ref={chartRef} className="wallet-overview-chart" />;
};

export default WalletOverviewChart; 